import React, { memo } from 'react'
import styled from 'styled-components'
import ReactCountryFlag from 'react-country-flag'
import List from 'rc-virtual-list'
import { Tooltip } from 'react-tippy'

import { Desktop, Mobile } from '../../utils/media'
import { palette, mediaQueries, shadows, fonts } from '../../styles/variables'
import ReturnRight from '../../styles/assets/tableIcons/right_arrow.svg'
import ReturnRightHover from '../../styles/assets/tableIcons/right_arrow_hover.svg'
import globeIcon from '../../styles/assets/globe.svg'
import searchIcon from '../../styles/assets/tableIcons/search.svg'
import Icon, { ArrowIcon, Line } from '../icon/Icon'
import { getAvailableLanguages, isGlobal } from '../../utils/locations'
import { Text } from '../topography'

export const defaultTheme = {
  color: palette.darkGray70,
  bgColor: palette.white,
  borderColor: palette.lightGray,
  height: 50,
  listHeight: 190,
}

export const LanguageSelectorWrapper = styled.div((props) => ({
  position: 'relative',
  zIndex: 'auto',
  flexGrow: 1,
  cursor: 'pointer',
  flex: 3,
  maxWidth: '100%',
  [mediaQueries.mobile]: {
    marginRight: 0,
    boxSizing: 'border-box',
    marginLeft: '1px',
  },
  '& .country-selector': {
    backgroundColor: props.theme.bgColor,
    height: props.theme.height,
    border: `1px solid ${props.theme.borderColor}`,
    color: props.theme.color,
    '& .arrow': {
      borderColor: props.theme.color,
    },
    [mediaQueries.mobile]: {
      display: 'flex',
      height: '48px',
      alignItems: 'center',
      margin: 0,
    },
  },
  '& .country-wrapper': {
    height: props.theme.height,
    [mediaQueries.mobile]: {
      height: '48px',
      width: '100%',
    },
  },
  '& .country-dropdown': {
    border: `1px solid ${props.theme.borderColor}`,
    zIndex: 104,
    [mediaQueries.mobile]: {
      width: '100%',
      marginLeft: 0,
    },
  },
}))

export const SelectedCountry = styled.div((props) => {
  if (props.opened) {
    return {
      backgroundColor: palette.lightGray20,
      color: palette.darkGray70,
      width: '100%',
      height: defaultTheme.height,
      '& .arrow': {
        borderColor: palette.darkGray70,
        transform: 'translate(6px, 1px) rotate(135deg)',
      },
    }
  }
  return {
    backgroundColor: palette.orange70,
    color: palette.white,
    width: '100%',
    height: defaultTheme.height,
  }
})

const LanguageWrapper = styled.div((props) => ({
  display: 'flex',
  cursor: props.disabled ? 'not-allowed' : 'pointer',
  color: props.disabled ? palette.lightGray : palette.darkGray,
  height: defaultTheme.height,
  alignItems: 'center',
  padding: '0 16px',
  backgroundColor: props.active ? palette.lightGray20 : palette.transparent,
  [props.hoverEffect && ':hover']: {
    backgroundColor: palette.lightGray20,
    color: palette.orange,
  },
}))

const CountryWrapper = styled.div((props) => ({
  display: 'flex',
  cursor: props.disabled ? 'not-allowed' : 'pointer',
  color: props.disabled ? palette.lightGray : palette.darkGray,
  height: defaultTheme.height,
  alignItems: 'center',
  padding: '0 16px',
  backgroundColor: props.active ? palette.lightGray20 : palette.transparent,
  [props.hoverEffect && ':hover']: {
    backgroundColor: palette.lightGray20,
  },
  [props.breaklineIndex && `&:nth-child(${props.breaklineIndex})`]: {
    alignItems: 'flex-start',
    paddingTop: '18px',
  },
  [props.breaklineIndex && `&:nth-child(${props.breaklineIndex + 1})`]: {
    alignItems: 'flex-end',
    paddingBottom: '18px',
  },
}))

export const CountryName = styled.div({
  fontSize: '15px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flex: 100,
  marginLeft: '15px',
  [mediaQueries.mobile]: {
    fontSize: '12px',
  },
})

export const CountryDropdown = styled.div((props) => ({
  position: 'absolute',
  zIndex: 100,
  marginTop: '4px',
  background: palette.white,
  borderRadius: '2px',
  overflow: 'auto',
  width: '100%',
  display: props.hide ? 'none' : 'block',
  boxShadow: shadows.shadowB,
}))

export const SearchInput = styled.input.attrs({
  autoComplete: 'do-not-autofill',
})({
  width: '100%',
  height: defaultTheme.height,
  backgroundColor: 'transparent',
  border: 'none',
  borderBottom: `1px solid ${palette.lightGray}`,
  color: palette.darkGray70,
  fontSize: '15px',
  padding: '0 20px 0 35px',
  background: `url(${searchIcon}) no-repeat 13px 16px`,
  '::placeholder': {
    color: palette.lightGray50,
  },
})

const EnterButton = styled.img({
  position: 'absolute',
  width: '22px',
  height: '20px',
  minWidth: '22px',
  right: 10,
  top: '25%',
  cursor: 'pointer',
  background: `url(${ReturnRight}) no-repeat`,
  ':hover': {
    background: `url(${ReturnRightHover}) no-repeat`,
  },
})

const flagStyle = {
  marginTop: '-3px',
  width: '1.3333em',
  height: '1em',
}

export const Label = styled(Text)({
  color: palette.darkGray,
  fontSize: '14px',
  lineHeight: '14px',
  fontFamily: fonts.primaryBook,
  marginBottom: '12px',
})

export const Language = memo((props) => (
  <LanguageWrapper
    data-testid={props.testId}
    active={props.active}
    className="country-wrapper"
    onClick={props.onClick}
    style={props.style}
    hoverEffect={props.hoverEffect}
    breaklineIndex={props.breaklineIndex}
  >
    <CountryName style={{ marginLeft: 0 }}>{props.name}</CountryName>
    {props.showArrow && (
      <ArrowIcon down className="arrow" style={{ marginRight: '5px' }} />
    )}
  </LanguageWrapper>
))

export const LanguageWithTooltip = memo((props) => (
  <LanguageWrapper
    data-testid={props.testId}
    active={props.active}
    className="country-wrapper"
    onClick={props.onClick}
    style={props.style}
    hoverEffect={props.hoverEffect}
    breaklineIndex={props.breaklineIndex}
  >
    <Tooltip
      disabled={!props.tooltipText}
      arrow
      position="top"
      theme="dark"
      boundary="viewport"
      popperOptions={{
        modifiers: {
          preventOverflow: {
            enabled: false,
          },
          flip: { enabled: false },
          hide: {
            enabled: false,
          },
          addZIndex: {
            enabled: true,
            order: 810,
            fn: (data) => ({
              ...data,
              styles: {
                ...data.styles,
                zIndex: 100001,
              },
            }),
          },
        },
      }}
      html={props.tooltipText}
      style={{ display: 'flex', alignItems: 'center', width: '100%' }}
    >
      <CountryName style={{ marginLeft: 0 }}>{props.name}</CountryName>
      {props.showArrow && (
        <ArrowIcon down className="arrow" style={{ marginRight: '5px' }} />
      )}
    </Tooltip>
  </LanguageWrapper>
))

export const Country = memo((props) => (
  <CountryWrapper
    active={props.active}
    className="country-wrapper"
    onClick={props.onClick}
    style={props.style}
    hoverEffect={props.hoverEffect}
    breaklineIndex={props.breaklineIndex}
    disabled={props.disabled}
  >
    {isGlobal(props.code) ? (
      <Icon src={globeIcon} />
    ) : (
      props.code && (
        <ReactCountryFlag countryCode={props.code} svg style={flagStyle} />
      )
    )}

    <CountryName style={{ marginLeft: props.code ? '15px' : '0px' }}>
      {props.name}
    </CountryName>
    {props.showArrow && (
      <ArrowIcon down className="arrow" style={{ marginRight: '5px' }} />
    )}
    {props.enableIcon && <EnterButton />}
  </CountryWrapper>
))

export const CountryWithTooltip = memo((props) => (
  <CountryWrapper
    data-testid={props.testId}
    active={props.active}
    className="country-wrapper"
    onClick={props.onClick}
    style={props.style}
    hoverEffect={props.hoverEffect}
    breaklineIndex={props.breaklineIndex}
    disabled={props.disabled}
  >
    <Tooltip
      disabled={!props.tooltipText}
      arrow
      position={props.tooltipPosition || 'top'}
      theme="dark"
      boundary="viewport"
      popperOptions={{
        modifiers: {
          preventOverflow: {
            enabled: false,
          },
          flip: { enabled: false },
          hide: {
            enabled: false,
          },
          addZIndex: {
            enabled: true,
            order: 810,
            fn: (data) => ({
              ...data,
              styles: {
                ...data.styles,
                zIndex: 100001,
              },
            }),
          },
        },
      }}
      html={props.tooltipText}
      style={{ display: 'flex', alignItems: 'center', width: '100%' }}
    >
      {isGlobal(props.code) ? (
        <Icon src={globeIcon} />
      ) : (
        <ReactCountryFlag countryCode={props.code} svg style={flagStyle} />
      )}

      <CountryName>{props.name}</CountryName>
      {props.showArrow && (
        <ArrowIcon down className="arrow" style={{ marginRight: '5px' }} />
      )}
      {props.enableIcon && <EnterButton />}
    </Tooltip>
  </CountryWrapper>
))

export const LoadingWrapper = styled.div((props) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  width: '100%',
  height: props.height ? `${props.height}px` : '230px',
}))

export const LanguageList = memo(
  ({
    activeIndex,
    options,
    onClick,
    getValue = (option) => option.name,
    width,
    height,
    showArrow = false,
  }) => {
    return (
      <List
        initialScrollOffset={activeIndex * 50}
        scrollToItem={options.length > 0 ? activeIndex : null}
        width={width || '100%'}
        height={height || 230}
        data={options}
        itemHeight={50}
        overscanCount={100}
      >
        {(item, index) => (
          <React.Fragment key={`${options[index]}`}>
            <Desktop>
              <LanguageWithTooltip
                testId="language-list-item"
                active={index === activeIndex}
                hoverEffect
                showArrow={showArrow}
                style={{
                  height: 50,
                }}
                name={getValue(item)}
                onClick={(event) => onClick(event, item, index)}
                tooltipText={
                  <LocationDisableText text={`<b>${getValue(item)}</b>`} />
                }
              />
            </Desktop>
            <Mobile>
              <Language
                name={getValue(item)}
                onClick={(event) => onClick(event, item)}
              />
            </Mobile>
          </React.Fragment>
        )}
      </List>
    )
  },
)

export const LocationLanguageList = memo(
  ({
    activeIndex,
    options,
    onClick,
    getValue = (option) => option.name,
    width,
    height,
    showArrow = false,
    enableIcon = false,
    breakLineIndex = false,
    tooltipPosition,
    getDisabledTooltipMessage = () => null,
  }) => {
    return (
      <List
        scrollToItem={options.length > 0 ? activeIndex : undefined}
        width={width || '100%'}
        height={height || 230}
        data={options}
        itemHeight={55}
        overscanCount={100}
      >
        {(item, index) => (
          <React.Fragment key={`${item.id}_${item.languageCode}`}>
            <Desktop>
              <CountryWithTooltip
                testId="location-list-item"
                active={index === activeIndex}
                hoverEffect
                showArrow={showArrow}
                style={{
                  height: (() =>
                    breakLineIndex &&
                    (index === breakLineIndex - 1 || index === breakLineIndex)
                      ? 55
                      : 50)(),
                }}
                enableIcon={enableIcon}
                name={getValue(item)}
                code={item.countryIsoCode.substring(0, 2)}
                onClick={(event) =>
                  !item.disabled && onClick(event, item, index)
                }
                breaklineIndex={breakLineIndex}
                disabled={item.disabled}
                tooltipPosition={tooltipPosition}
                tooltipText={
                  <LocationDisableText
                    text={
                      item.disabled
                        ? getDisabledTooltipMessage()
                        : `<b>${getValue(item)}</b>`
                    }
                  />
                }
              />
              {index === breakLineIndex && (
                <Line
                  style={{
                    width: '86%',
                    height: '1px',
                    left: '10%',
                  }}
                />
              )}
            </Desktop>
            <Mobile>
              <Language
                name={getValue(item)}
                code={item.countryIsoCode.substring(0, 2)}
                onClick={(event) => onClick(event, item)}
                hasTopBreakline={!!breakLineIndex}
              />
            </Mobile>
          </React.Fragment>
        )}
      </List>
    )
  },
)
export const LocationDisableText = ({ text }) => (
  <div style={{ maxWidth: '270px', padding: '10px 5px' }}>
    <Text
      fontSize="12px"
      lineHeight="16px"
      color={palette.white}
      dangerouslySetInnerHTML={{
        __html: text,
      }}
      boldColor={palette.white}
    />
  </div>
)

export const LocationList = memo((props) => {
  const {
    activeIndex,
    options,
    onClick,
    getValue = (option) => option.name,
    width,
    height,
    showArrow = false,
    enableIcon = false,
    getSupportedLocationTooltip = () => null,
    getUnsupportedLocationTooltip = () => null,
    useCompetitorLocations = false,
    language = null,
  } = props
  const unSupportedIndex = options.supported.length
  const allOptions = options.supported.concat(options.unSupported)

  const getTooltipText = ({
    index,
    availableLanguages,
    optionValue,
    filteredAvailableLanguages,
  }) => {
    if (index >= unSupportedIndex) {
      return (
        <LocationDisableText
          text={getUnsupportedLocationTooltip(availableLanguages, optionValue)}
        />
      )
    }

    if (filteredAvailableLanguages?.length) {
      return (
        <LocationDisableText
          text={getSupportedLocationTooltip(
            filteredAvailableLanguages,
            optionValue,
          )}
        />
      )
    }

    return <LocationDisableText text={`<b>${optionValue}</b>`} />
  }

  return (
    <List
      scrollToItem={allOptions.length > 0 ? activeIndex : null}
      width={width || '100%'}
      height={height || 230}
      itemCount={allOptions.length}
      data={allOptions}
      itemHeight={55}
      overscanCount={100}
    >
      {(item, index) => {
        const availableLanguages = getAvailableLanguages(
          item.countryIsoCode,
          useCompetitorLocations,
        )
        const filteredAvailableLanguages =
          language && availableLanguages.length
            ? availableLanguages.filter(({ code }) => code !== language)
            : availableLanguages
        const optionValue = getValue(item)
        return (
          <React.Fragment key={`${item.id}`}>
            <CountryWithTooltip
              testId="location-list-item"
              active={index === activeIndex}
              hoverEffect
              showArrow={showArrow}
              style={{
                height: (() =>
                  index === unSupportedIndex - 1 || index === unSupportedIndex
                    ? 55
                    : 50)(),
              }}
              enableIcon={enableIcon}
              name={optionValue}
              code={item.countryIsoCode.substring(0, 2)}
              onClick={(event) =>
                index < unSupportedIndex && onClick(event, item, index)
              }
              breaklineIndex={unSupportedIndex}
              disabled={index >= unSupportedIndex}
              tooltipText={getTooltipText({
                index,
                availableLanguages,
                optionValue,
                filteredAvailableLanguages,
              })}
            />
            {options.supported.length > 0 && index === unSupportedIndex && (
              <Line
                style={{
                  width: '86%',
                  height: '1px',
                  left: '10%',
                }}
              />
            )}
          </React.Fragment>
        )
      }}
    </List>
  )
})
